import styled from '@emotion/styled'
import {
  type ComponentProps,
  type MouseEvent,
  memo,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import { useDialogVisibility } from '../../code-only/hooks/use-dialog-visibility.ts'
import { useUiContext } from '../../code-only/hooks/use-screen-variant.ts'
import type { Icon } from '../../foundations/index.ts'
import { spacing } from '../../foundations/spacing-tokens.ts'
import { Button, type ButtonSize } from '../button/index.ts'

import { DialogMenuList } from './dialog-menu-list.tsx'
import { FloatingMenuList } from './floating-menu-list.tsx'

export type MenuItem =
  | {
      icon?: ComponentProps<typeof Icon>['icon']
      label: string
      value: string
    }
  | {
      divider: true
    }

export type MenuProps = {
  menuItems: MenuItem[]
  size: ButtonSize
  position: 'left' | 'right'
  onChange: (value: string | null) => void
  buttonAccessoryLeft?: ReactNode
  buttonAccessoryRight?: ReactNode
  buttonLabel?: string
  buttonMaxWidth?: string
  forceOpen?: boolean
}

export const Menu = memo<MenuProps>(function Menu(props) {
  const {
    menuItems,
    size,
    position,
    onChange,
    buttonAccessoryLeft,
    buttonAccessoryRight,
    buttonLabel,
    buttonMaxWidth,
    forceOpen = false,
  } = props

  const isMobile = useUiContext() === 'mobile'
  const { show, hide, visible } = useDialogVisibility('topbar-menu')
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [buttonWidth, setButtonWidth] = useState<number>(0)

  const open = forceOpen || visible

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  useEffect(() => {
    if (buttonRef.current) {
      setButtonWidth(buttonRef.current.offsetWidth)
    }
  }, [buttonLabel, buttonAccessoryLeft, buttonAccessoryRight])

  const handleClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget)
      show()
    },
    [setAnchorEl, show],
  )
  const handleClose = useCallback(() => {
    hide()
  }, [hide])

  return (
    <div>
      <StyledMenuButton
        ref={buttonRef}
        variant={isMobile ? 'text' : 'secondary'}
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        maxWidth={buttonMaxWidth}
        size={size}
      >
        {buttonAccessoryLeft}
        {isMobile ? null : (
          <>
            {buttonLabel}
            {buttonAccessoryRight}
          </>
        )}
      </StyledMenuButton>

      {isMobile ? (
        <DialogMenuList
          menuItems={menuItems}
          anchorElement={anchorEl}
          open={open}
          handleClose={handleClose}
          onChange={onChange}
        />
      ) : (
        <FloatingMenuList
          menuItems={menuItems}
          anchorElement={anchorEl}
          open={open}
          handleClose={handleClose}
          onChange={onChange}
          buttonWidth={buttonWidth}
          position={position}
          size={size}
        />
      )}
    </div>
  )
})

const StyledMenuButton = styled(Button)<{ maxWidth?: string | undefined }>`
  ${({ maxWidth = undefined }) => (maxWidth ? `max-width: ${maxWidth};` : ``)}
  overflow: hidden;
  padding: 0;

  & > div > span {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: ${spacing[40]};
    padding: ${spacing[40]};
    max-width: 240px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
`
